import { IntlKey } from 'locales';
import { isEqual, unionWith } from 'lodash-es';
import { ProductMetadata } from 'models/batch/import-metadata';
import { FieldMetadata } from 'models/forms/fields';
import { ProductVariation } from 'models/productsReferential';

import { ImportedProductRow } from './fieldProcessingUtils';
import { DataMap } from './parseFileUtils';

export type TranslationMap = Record<IntlKey, string>;
export type ProductReverseMap = Record<string, string>;

/**
 * Creates a reverse mapping from translated product names to their keys
 */
export function createProductReverseMap(translationMap: TranslationMap): ProductReverseMap {
  const productReverseMap: ProductReverseMap = {};
  for (const [intlKey, value] of Object.entries(translationMap)) {
    if (intlKey.startsWith('quote.products.')) {
      productReverseMap[value] = intlKey.replace(/^quote\.products\./, '');
    }
  }
  return productReverseMap;
}

/**
 * Creates a nested mapping structure for product field metadata
 */
export function createProductFieldMetadataMap(
  productsMetadata: ProductMetadata[],
  translationMap: Record<IntlKey, string>,
) {
  const productFieldMetadataMap: Record<string, Record<string, Record<string, FieldMetadata>>> = {};

  for (const metadata of productsMetadata) {
    const { productType, productSubType, fieldsMetadata: fieldsMetadataMap } = metadata;

    if (!productFieldMetadataMap[productType]) {
      productFieldMetadataMap[productType] = {};
    }
    if (!productFieldMetadataMap[productType][productSubType]) {
      productFieldMetadataMap[productType][productSubType] = {};
    }

    Object.values(fieldsMetadataMap).forEach(fieldMetadata => {
      const intlKey = `form.${fieldMetadata.translationKey}` as IntlKey;
      const label = translationMap[intlKey] ?? fieldMetadata.id;

      if (label) {
        productFieldMetadataMap[productType][productSubType][label.toLocaleLowerCase()] = fieldMetadata;
      }
    });
  }

  return productFieldMetadataMap;
}

type ProductVariationTranslationKey = 'form.ProductType' | 'form.ProductSubtype' | 'form.Wrapper';

export function getProductVariation(dataMap: DataMap, translationMap: TranslationMap, productMap: ProductReverseMap) {
  const getMappedValue = (translationKey: ProductVariationTranslationKey) => {
    const label = translationMap[translationKey]?.toLocaleLowerCase();
    const value = label ? dataMap[label] : undefined;
    return typeof value === 'string' ? productMap[value] : undefined;
  };
  return {
    productType: getMappedValue('form.ProductType'),
    productSubType: getMappedValue('form.ProductSubtype'),
    wrapper: getMappedValue('form.Wrapper'),
  };
}

export function getUniqueProductVariations(rows: ImportedProductRow[]): ProductVariation[] {
  const productVariations = rows.map(row => ({
    productType: row.productType,
    productSubType: row.productSubType,
    wrapper: row.wrapper,
  }));

  return unionWith(productVariations, isEqual);
}

export function createProductRows(
  dataMaps: DataMap[],
  translationMap: TranslationMap,
  productMap: ProductReverseMap,
): ImportedProductRow[] {
  return dataMaps.map(dataMap => {
    const productVariation = getProductVariation(dataMap, translationMap, productMap);
    return {
      productType: productVariation.productType ?? '',
      productSubType: productVariation.productSubType ?? '',
      wrapper: productVariation.wrapper ?? '',
      dataMap,
    };
  });
}
